import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Container,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Chip,
  IconButton,
  Fade,
  Tooltip,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { GetInvoiceStatuses } from "../../../services/DashboardService";
import "./Panels.css";

export default function InvoicesPanel({ startDate, endDate, refreshKey }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const max = data.length ? Math.max(...data.map((d) => d.count)) : 0;
  const total = data.reduce((sum, item) => sum + item.count, 0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetInvoiceStatuses(startDate, endDate);
      setData(res || []);
      setLastUpdated(new Date());
    } catch (e) {
      console.error(e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const getStatusConfig = () => ({
    gradient: "linear-gradient(135deg, #007DD1 0%, #4BA4DF 100%)",
    color: "#007DD1",
    icon: <ReceiptIcon fontSize="small" />,
    lightBg: "rgba(0, 125, 209, 0.08)",
  });

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight={600} color="primary.main">
            Invoice Status Dashboard
          </Typography>
          <Tooltip title="Real-time invoice status distribution">
            <InfoOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />
          </Tooltip>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh data">
            <IconButton onClick={loadData} size="small" disabled={loading}>
              <RefreshIcon sx={{ fontSize: 18 }} className={loading ? "spinning" : ""} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Status Cards */}
      <Grid container spacing={2}>
        {data.map((row, index) => {
          const config = getStatusConfig(row.status);
          const percentage = max ? Math.round((row.count / max) * 100) : 0;
          const sharePercentage = total ? ((row.count / total) * 100).toFixed(1) : 0;

          return (
            <Grid item xs={12} sm={6} lg={4} key={row.status || `status-${index}`}>
              <Fade in timeout={200 + index * 80}>
                <Card
                  elevation={1}
                  sx={{
                    borderRadius: 2,
                    transition: "transform 0.2s ease",
                    "&:hover": { transform: "translateY(-3px)", boxShadow: 3 },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    {/* Top Section */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: config.gradient,
                          color: "#fff",
                        }}
                      >
                        {config.icon}
                      </Box>

                      <Chip
                        label={`${sharePercentage}%`}
                        size="small"
                        sx={{
                          bgcolor: config.lightBg,
                          color: config.color,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          height: 20,
                        }}
                      />
                    </Box>

                    {/* Status Title */}
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ color: config.color, mb: 0.5 }}
                    >
                      {row.status || "Unknown"}
                    </Typography>

                    {/* Count */}
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, mb: 0.5 }}>
                      {row.count?.toLocaleString() || 0}
                    </Typography>

                    {/* Progress Bar */}
                    <Box sx={{ mb: 1 }}>
                      <div className="progress-bar-small">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${percentage}%`,
                            background: config.gradient,
                          }}
                        />
                      </div>
                    </Box>

                    {/* Metrics */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{ color: config.color, fontWeight: 500 }}
                      >
                        <TrendingUpIcon sx={{ fontSize: 12, mr: 0.5 }} />
                        {percentage}% of max
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Rank #{index + 1}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          );
        })}

        {!data?.length && (
          <Grid item xs={12}>
            <Card sx={{ textAlign: "center", py: 4 }}>
              <ReceiptIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
              <Typography variant="subtitle1" color="text.secondary">
                No Invoice Data Available
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Try adjusting your date range.
              </Typography>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}
